import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Mail, Send, FileText, Plus, ArrowRight, Loader2, Database, CheckCircle2, Clock, LayoutDashboard, Shield, ExternalLink } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { campaignsApi, signupFormsApi, teamsApi, usersApi, CampaignRecord, SignupFormRecord, TeamRecord, UserRecord } from '../services/creatorApi';
import { crmMailingListsApi, CrmMailingList } from '../services/crmApi';
import { contactsApi } from '../services/contactsApi';
import { toast } from 'sonner';
export function Dashboard() {
  const {
    currentWorkspace,
    currentUser,
    currentSubRegion
  } = useWorkspace();
  const [loading, setLoading] = useState(true);
  // Data state
  const [stats, setStats] = useState({
    contacts: 0,
    lists: 0,
    campaigns: 0,
    forms: 0
  });
  const [recentCampaigns, setRecentCampaigns] = useState<CampaignRecord[]>([]);
  const [recentLists, setRecentLists] = useState<CrmMailingList[]>([]);
  // Super Admin Data
  const [allTeams, setAllTeams] = useState<TeamRecord[]>([]);
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch data in parallel
        const [campaignsResult, formsResult, listsData, contactCountResult, teamsResult, usersResult] = await Promise.all([campaignsApi.list(), signupFormsApi.list(), crmMailingListsApi.list(), contactsApi.count({
          team: currentWorkspace.name || undefined,
          subRegion: currentSubRegion || undefined
        }), currentUser.Role === 'Super Admin' ? teamsApi.list() : Promise.resolve([]), currentUser.Role === 'Super Admin' ? usersApi.list() : Promise.resolve([])]);
        const campaignsData = campaignsResult;
        const formsData = formsResult;
        const teamsData = teamsResult;
        const usersData = usersResult;
        // Filter by current workspace/team
        const teamCampaigns = campaignsData.filter(c => c.Team === currentWorkspace.name && (!currentSubRegion || !c.Region || c.Region === currentSubRegion));
        const teamForms = formsData.filter(f => f.Team === currentWorkspace.name && (!currentSubRegion || !f.Region || f.Region === currentSubRegion));
        const teamLists = listsData.filter(l => l.Team === currentWorkspace.name && (!currentSubRegion || !l.Region || l.Region === currentSubRegion));
        // Sort campaigns by date (newest first)
        const sortedCampaigns = [...teamCampaigns].sort((a, b) => {
          return (b.Exported_At || b.ID).localeCompare(a.Exported_At || a.ID);
        }).slice(0, 5);
        setRecentCampaigns(sortedCampaigns);
        setRecentLists(teamLists.slice(0, 5));
        setStats({
          contacts: contactCountResult,
          lists: teamLists.length,
          campaigns: teamCampaigns.length,
          forms: teamForms.length
        });
        if (currentUser.Role === 'Super Admin') {
          setAllTeams(teamsData);
          setAllUsers(usersData);
        }
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [currentWorkspace.name, currentSubRegion, currentUser.Role]);
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Sent':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-3681" data-mp="2qby32eo9xlog">
            <CheckCircle2 className="h-3 w-3" data-id="element-3682" data-mp="2geti2i65zw98" />
            Sent
          </span>;
      case 'Exported':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-3683" data-mp="3e1afroit8bvi">
            <Database className="h-3 w-3" data-id="element-3684" data-mp="34c8ylv0j3ofy" />
            Exported
          </span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-3685" data-mp="2ze7er7va3l3f">
            <FileText className="h-3 w-3" data-id="element-3686" data-mp="pa0c0ui2p60" />
            Draft
          </span>;
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]" data-id="element-3687" data-mp="45mn7saci7lu">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3688" data-mp="47fxdhk639yl" />
      </div>;
  }
  return <div className="space-y-8 pb-12 animate-in fade-in duration-500" data-id="element-3689" data-mp="8914ema0su0v">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3690" data-mp="d4q6aw9iin0c">
        <div data-id="element-3691" data-mp="i6p9o6595jxa">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-3692" data-mp="i31el2k5spgg">
            Dashboard
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-3693" data-mp="n2ebkg4qvcwr">
            Welcome back, {currentUser.User_Name}. Here's what's happening in{' '}
            {currentWorkspace.name}.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-3694" data-mp="vylwpvs2d3mt">
          <span className="text-sm text-gray-500 bg-white px-3 py-1.5 rounded-md border border-gray-200 shadow-sm flex items-center gap-2" data-id="element-3695" data-mp="10ukh78a9u1hy">
            <Clock className="h-4 w-4" data-id="element-3696" data-mp="15sm11vfiu4uh" />
            {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3697" data-mp="g4gl4wth0mg6">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-3698" data-mp="ux0kdmdi29sc">
          <div className="flex items-center justify-between" data-id="element-3699" data-mp="q2nyz6sgqody">
            <div data-id="element-3700" data-mp="13v488pbyjinb">
              <p className="text-sm font-medium text-gray-500" data-id="element-3701" data-mp="13ukp55akmo7p">
                Total Contacts
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3702" data-mp="ywstq1apwjyr">
                {stats.contacts.toLocaleString()}
              </p>
            </div>
            <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-3703" data-mp="18uywsm89fw57">
              <Users className="h-5 w-5 text-blue-600" data-id="element-3704" data-mp="3fl6k4c4a9qku" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-3705" data-mp="3amvaa1dzng2m">
            <Link to="/contacts" className="flex items-center gap-1" data-id="element-3706" data-mp="1q418epv0qxlu">
              View all contacts <ArrowRight className="h-4 w-4" data-id="element-3707" data-mp="1v3e3fxw1nazi" />
            </Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-3708" data-mp="1gf14qt5kne1b">
          <div className="flex items-center justify-between" data-id="element-3709" data-mp="1ld2o7f6wtfdb">
            <div data-id="element-3710" data-mp="1lbhutswc02h1">
              <p className="text-sm font-medium text-gray-500" data-id="element-3711" data-mp="17ebavuzvfxq8">Mailing Lists</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3712" data-mp="17gfq39380j5t">
                {stats.lists}
              </p>
            </div>
            <div className="h-10 w-10 bg-purple-50 rounded-lg flex items-center justify-center" data-id="element-3713" data-mp="xgov76ar0257">
              <Mail className="h-5 w-5 text-purple-600" data-id="element-3714" data-mp="12egoo4r1jwbi" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-purple-600 hover:text-purple-700 font-medium" data-id="element-3715" data-mp="2rtephp513dd4">
            <Link to="/mailing-lists" className="flex items-center gap-1" data-id="element-3716" data-mp="2wt9grtspu4yp">
              Manage lists <ArrowRight className="h-4 w-4" data-id="element-3717" data-mp="1jmob4gdq1hvg" />
            </Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-3718" data-mp="1fktdpmywvomy">
          <div className="flex items-center justify-between" data-id="element-3719" data-mp="1at1i2y0wespy">
            <div data-id="element-3720" data-mp="15urmwnf6bs4c">
              <p className="text-sm font-medium text-gray-500" data-id="element-3721" data-mp="15v9s49jtdgoe">Campaigns</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3722" data-mp="15sxa5we236bc">
                {stats.campaigns}
              </p>
            </div>
            <div className="h-10 w-10 bg-green-50 rounded-lg flex items-center justify-center" data-id="element-3723" data-mp="vwar9d5aipby">
              <Send className="h-5 w-5 text-green-600" data-id="element-3724" data-mp="ru74hsd67ybd" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-green-600 hover:text-green-700 font-medium" data-id="element-3725" data-mp="mwfc6x8m6a6n">
            <Link to="/campaigns" className="flex items-center gap-1" data-id="element-3726" data-mp="2m99ojajgao6w">
              View campaigns <ArrowRight className="h-4 w-4" data-id="element-3727" data-mp="2h9dkjbar6wrc" />
            </Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-3728" data-mp="2otphp3lachs3">
          <div className="flex items-center justify-between" data-id="element-3729" data-mp="2trr15pmmij43">
            <div data-id="element-3730" data-mp="2tq6awc7cdmc1">
              <p className="text-sm font-medium text-gray-500" data-id="element-3731" data-mp="2yjhdu2919iyo">Signup Forms</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3732" data-mp="2ylllmva1z248">
                {stats.forms}
              </p>
            </div>
            <div className="h-10 w-10 bg-orange-50 rounded-lg flex items-center justify-center" data-id="element-3733" data-mp="25vda3ma0vfym">
              <FileText className="h-5 w-5 text-orange-600" data-id="element-3734" data-mp="2asv9om25gity" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-orange-600 hover:text-orange-700 font-medium" data-id="element-3735" data-mp="2fs86nzmqj6a9">
            <Link to="/forms" className="flex items-center gap-1" data-id="element-3736" data-mp="2jul8we29wm2a">
              Edit forms <ArrowRight className="h-4 w-4" data-id="element-3737" data-mp="3r86iucd7m8fp" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-3738" data-mp="3v86x9x0b6jmr">
        {/* Recent Campaigns - 2/3 width */}
        <div className="lg:col-span-2 space-y-6" data-id="element-3739" data-mp="1l49v0xx312fs">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3740" data-mp="1q3mfxd4e7zfq">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-3741" data-mp="1uuw8oml8nivu">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-3742" data-mp="1ztfv0o3quwoq">
                Recent Campaigns
              </h2>
              <Link to="/campaigns" className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-3743" data-mp="126qlzuamjjtd">
                View All
              </Link>
            </div>
            <div className="overflow-x-auto" data-id="element-3744" data-mp="1c33b0ev859hs">
              {recentCampaigns.length > 0 ? <table className="min-w-full divide-y divide-gray-200" data-id="element-3745" data-mp="1h496ulk6lcvj">
                  <thead className="bg-gray-50" data-id="element-3746" data-mp="2njjv4lhq4eaz">
                    <tr data-id="element-3747" data-mp="2niryr8gaycjn">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3748" data-mp="2nlmjl1389zdl">
                        Campaign Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3749" data-mp="gu88ofwxvrt6">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3750" data-mp="73x0gh682wgh">
                        Recipients
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3751" data-mp="3tcogmuk14m6w">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-3752" data-mp="3kc9vpwaz993x">
                    {recentCampaigns.map(campaign => <tr key={campaign.ID} className="hover:bg-gray-50 transition-colors" data-id="element-3753" data-mp="3fezwjoxsdj2w">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3754" data-mp="1dmlfes32ginj">
                          <div className="text-sm font-medium text-gray-900" data-id="element-3755" data-mp="380z502qg4thz">
                            {campaign.Campaign_Name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-3756" data-mp="3cu1yybz4y4yp">
                            {campaign.Email_Subject}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3757" data-mp="332p6pj9fdcs5">
                          {getStatusBadge(campaign.Status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-3758" data-mp="3vrcvhotdukag">
                          {campaign.Recipient_Count?.toLocaleString() || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3759" data-mp="3ls46799gptuf">
                          {formatDate(campaign.Exported_At)}
                        </td>
                      </tr>)}
                  </tbody>
                </table> : <div className="p-8 text-center text-gray-500" data-id="element-3760" data-mp="sflt131iwc7m">
                  No campaigns found.{' '}
                  <Link to="/campaigns/new" className="text-blue-600 hover:underline" data-id="element-3761" data-mp="nhtwfvpxo1x3">
                    Create your first campaign
                  </Link>
                </div>}
            </div>
          </div>

          {/* Super Admin Team Overview */}
          {currentUser.Role === 'Super Admin' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3762" data-mp="4r2nuwdrsfjr">
              <div className="p-6 border-b border-gray-200 flex items-center gap-2" data-id="element-3763" data-mp="iih979v5gstz">
                <Shield className="h-5 w-5 text-purple-600" data-id="element-3764" data-mp="drhb41aao4pw" />
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-3765" data-mp="dpxw0e0djzpp">
                  Team Overview (Super Admin)
                </h2>
              </div>
              <div className="overflow-x-auto" data-id="element-3766" data-mp="3i68qtuv28w2v">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3767" data-mp="3i3csi5faw5dw">
                  <thead className="bg-gray-50" data-id="element-3768" data-mp="3uvzk0g5nf20p">
                    <tr data-id="element-3769" data-mp="3odcgcft6fek">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3770" data-mp="3nlrrwnr2232">
                        Team Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3771" data-mp="ddwvpj7fob9y">
                        Region
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3772" data-mp="ifck9l8tkzx3">
                        Users
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3773" data-mp="rehdcc1m411v">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-3774" data-mp="2t7npoim0ox6z">
                    {allTeams.map(team => {
                  const teamUsers = allUsers.filter(u => u.Team === team.Team_Name);
                  return <tr key={team.ID} className="hover:bg-gray-50 transition-colors" data-id="element-3775" data-mp="2ls3dyupwhcf0">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-3776" data-mp="2bxbe1k8uidh2">
                            <div className="text-sm font-medium text-gray-900" data-id="element-3777" data-mp="39kadg2qx9zin">
                              {team.Team_Name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3778" data-mp="2zl1q3sqkbj59">
                            {team.Team_Regions?.map(r => r.Region_Name).join(', ') || 'All Regions'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-3779" data-mp="1j7bbhs1thchd">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-3780" data-mp="1j9fl8qmed5pk">
                              {teamUsers.length} users
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-3781" data-mp="18n2p5jc1dug">
                            <Link to="/users" className="text-blue-600 hover:text-blue-900" data-id="element-3782" data-mp="67q5skfr59zs">
                              Manage
                            </Link>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>

        {/* Sidebar / Quick Actions - 1/3 width */}
        <div className="space-y-6" data-id="element-3783" data-mp="34rq4b0dlhzro">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-3784" data-mp="34srfd4um2m9e">
            <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-3785" data-mp="39rm4spn3392h">
              Quick Actions
            </h3>
            <div className="space-y-3" data-id="element-3786" data-mp="2m24gqsb1ay9y">
              <Link to="/campaigns/new" className="flex items-center justify-between w-full p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors group" data-id="element-3787" data-mp="2m362prtl956g">
                <span className="flex items-center gap-3 font-medium" data-id="element-3788" data-mp="2v183c69etvt7">
                  <Send className="h-5 w-5" data-id="element-3789" data-mp="391isn4v2n2c9" />
                  Create Campaign
                </span>
                <Plus className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-3790" data-mp="34zxlm057xi9z" />
              </Link>

              <Link to="/forms/new" className="flex items-center justify-between w-full p-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors group" data-id="element-3791" data-mp="3ir2emje2zcbc">
                <span className="flex items-center gap-3 font-medium" data-id="element-3792" data-mp="3sqtaopiahzdj">
                  <FileText className="h-5 w-5" data-id="element-3793" data-mp="3nsro3ulnnhxb" />
                  New Signup Form
                </span>
                <Plus className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-3794" data-mp="28ecnfc5y2fum" />
              </Link>

              <Link to="/contacts" className="flex items-center justify-between w-full p-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors group" data-id="element-3795" data-mp="2dce3rpbzk12e">
                <span className="flex items-center gap-3 font-medium" data-id="element-3796" data-mp="2na2brgylr52i">
                  <Users className="h-5 w-5" data-id="element-3797" data-mp="1pq7oci08fj25" />
                  View Contacts
                </span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-3798" data-mp="1uo91ynew2q68" />
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-3799" data-mp="24dam8psgvtq0">
            <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-3800" data-mp="16y6e6ji0c6df">
              Active Mailing Lists
            </h3>
            <div className="space-y-4" data-id="element-3801" data-mp="1bwgbaznalh1o">
              {recentLists.length > 0 ? recentLists.map(list => <div key={list.id} className="flex items-center justify-between pb-3 border-b border-gray-100 last:border-0 last:pb-0" data-id="element-3802" data-mp="3p6zm5zzcyfa1">
                    <div className="min-w-0" data-id="element-3803" data-mp="3g59uct1kdebq">
                      <p className="text-sm font-medium text-gray-900 truncate" data-id="element-3804" data-mp="3b7qexpt4qvi7">
                        {list.Name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-3805" data-mp="369gmvo2pcb0t">
                        {list.Subscriber_Count || 0} subscribers
                      </p>
                    </div>
                    <Link to={`/mailing-lists/${list.id}`} className="text-gray-400 hover:text-blue-600" data-id="element-3806" data-mp="2xfk1ibhd32mg">
                      <ExternalLink className="h-4 w-4" data-id="element-3807" data-mp="2ni5wd80w209g" />
                    </Link>
                  </div>) : <p className="text-sm text-gray-500" data-id="element-3808" data-mp="2ik45w214uyny">No mailing lists found.</p>}
              <Link to="/mailing-lists" className="block text-center text-sm text-blue-600 hover:text-blue-700 font-medium pt-2" data-id="element-3809" data-mp="2h9okwhb1vs8d">
                View All Lists
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>;
}